import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';

const OSMAutocomplete = ({
  value,
  onChange,
  placeholder,
  className = "",
  onKeyDown,
  onBlur
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      updateDropdownPosition();
    }
  }, [showSuggestions, suggestions, updateDropdownPosition]);

  // Close dropdown on any scroll so the fixed position stays accurate
  useEffect(() => {
    if (!showSuggestions) return;
    const handleScroll = () => setShowSuggestions(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showSuggestions]);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch (error) {
      console.error('OSMAutocomplete: Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(newValue), 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') setShowSuggestions(false);
    if (onKeyDown) onKeyDown(e);
  };

  const handleBlur = (e) => {
    setTimeout(() => setShowSuggestions(false), 200);
    if (onBlur) onBlur(e);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const dropdown = showSuggestions && suggestions.length > 0 && createPortal(
    <div
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999,
      }}
      className="bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
    >
      {suggestions.map((suggestion, index) => (
        <div
          key={suggestion.place_id || index}
          className="px-3 py-2 cursor-pointer hover:bg-accent text-sm text-foreground border-b border-border last:border-b-0"
          onMouseDown={(e) => {
            e.preventDefault();
            handleSelectSuggestion(suggestion);
          }}
        >
          <div className="flex items-start gap-2">
            <i className="fas fa-map-marker-alt text-muted-foreground mt-0.5 text-xs"></i>
            <span className="line-clamp-2">{suggestion.display_name}</span>
          </div>
        </div>
      ))}
      <div className="px-3 py-1 text-xs text-muted-foreground bg-muted/50 text-center">
        Powered by OpenStreetMap
      </div>
    </div>,
    document.body
  );

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {dropdown}
    </div>
  );
};

export default OSMAutocomplete;
